// @ts-check
/** @typedef {import('../types/api').Influencer} Influencer */

import {
  CheckCircle,
  Edit2,
  ExternalLink,
  FileText,
  GitBranch,
  Instagram,
  Plus,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  TrendingUp,
  Users,
  X,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import CollaborationModal from '../components/influencers/CollaborationModal';
import ImportFromDMsModal from '../components/influencers/ImportFromDMsModal';
import InsightsTab from '../components/influencers/InsightsTab';
import OutreachModal from '../components/influencers/OutreachModal';
import PipelineTab from '../components/influencers/PipelineTab';
import TemplatesTab from '../components/influencers/TemplatesTab';
import LoadingScreen from '../components/LoadingScreen';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal, { ModalDrawer } from '../components/ui/Modal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import useRowSelection from '../hooks/useRowSelection';
import { reportBulkResult, runBulk } from '../services/bulkService';
import collaborationService from '../services/collaborationService';
import influencerService from '../services/influencerService';
import api from '../utils/apiClient';

// Status label + color maps
const OUTREACH_LABELS = {
  reached_out: {
    label: 'Reached Out',
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-700 dark:text-blue-300',
  },
  in_conversation: {
    label: 'In Conversation',
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  agreement_signed: {
    label: 'Ready to Collaborate',
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-700 dark:text-purple-300',
  },
  collaborated: {
    label: 'Collaborated',
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-700 dark:text-green-300',
  },
  inactive: { label: 'Inactive', bg: 'bg-muted', text: 'text-foreground/80' },
};

const COLLAB_STATUS_LABELS = {
  shortlisted: { label: 'Shortlisted', bg: 'bg-muted', text: 'text-muted-foreground' },
  outreach_pending: {
    label: 'Outreach Pending',
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  reached_out: {
    label: 'Reached Out',
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-700 dark:text-blue-300',
  },
  no_response: {
    label: 'No Response',
    bg: 'bg-orange-100 dark:bg-orange-900/40',
    text: 'text-orange-700 dark:text-orange-300',
  },
  responded: { label: 'Responded', bg: 'bg-cyan-100 dark:bg-cyan-900/40', text: 'text-cyan-700 dark:text-cyan-300' },
  in_negotiation: {
    label: 'In Negotiation',
    bg: 'bg-purple-100 dark:bg-purple-900/40',
    text: 'text-purple-700 dark:text-purple-300',
  },
  deal_closed: {
    label: 'Deal Closed',
    bg: 'bg-indigo-100 dark:bg-indigo-900/40',
    text: 'text-indigo-700 dark:text-indigo-300',
  },
  brief_sent: {
    label: 'Brief Sent',
    bg: 'bg-violet-100 dark:bg-violet-900/40',
    text: 'text-violet-700 dark:text-violet-300',
  },
  content_in_progress: {
    label: 'Content In Progress',
    bg: 'bg-blue-100 dark:bg-blue-900/40',
    text: 'text-blue-800 dark:text-blue-300',
  },
  content_submitted: {
    label: 'Content Submitted',
    bg: 'bg-teal-100 dark:bg-teal-900/40',
    text: 'text-teal-700 dark:text-teal-300',
  },
  revision_requested: {
    label: 'Revision Requested',
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-700 dark:text-amber-300',
  },
  content_approved: {
    label: 'Content Approved',
    bg: 'bg-lime-100 dark:bg-lime-900/40',
    text: 'text-lime-700 dark:text-lime-300',
  },
  content_live: {
    label: 'Content Live',
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-700 dark:text-green-300',
  },
  payment_pending: {
    label: 'Payment Pending',
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-700 dark:text-red-300',
  },
  payment_done: {
    label: 'Payment Done',
    bg: 'bg-emerald-100 dark:bg-emerald-900/40',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  campaign_complete: {
    label: 'Campaign Complete',
    bg: 'bg-green-200 dark:bg-green-900/60',
    text: 'text-green-800 dark:text-green-300',
  },
  declined: { label: 'Declined', bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-600 dark:text-red-400' },
  ghosted: { label: 'Ghosted', bg: 'bg-muted', text: 'text-muted-foreground' },
  on_hold: {
    label: 'On Hold',
    bg: 'bg-yellow-100 dark:bg-yellow-900/40',
    text: 'text-yellow-600 dark:text-yellow-400',
  },
  blacklisted: { label: 'Blacklisted', bg: 'bg-red-200 dark:bg-red-900/60', text: 'text-red-800 dark:text-red-300' },
};

const formatFollowers = (count) => {
  if (!count) return '\u2014';
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

const getTier = (count) => {
  if (!count) return null;
  if (count >= 1000000)
    return { label: 'Mega', bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300' };
  if (count >= 100000)
    return { label: 'Macro', bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300' };
  if (count >= 10000)
    return { label: 'Micro', bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' };
  return { label: 'Nano', bg: 'bg-muted', text: 'text-muted-foreground' };
};

const StatusBadge = ({ value, labels }) => {
  const info = labels[value] || { label: value, bg: 'bg-muted', text: 'text-foreground/80' };
  return <span className={`px-2 py-1 text-xs rounded-full font-medium ${info.bg} ${info.text}`}>{info.label}</span>;
};

const getInitials = (name) => {
  if (!name) return '?';
  const words = name.trim().split(' ');
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// ─── Main Component ───────────────────────────────────────────────────────────

// Outreach pipeline stages, as [value, label] (mirrors OutreachStatus in the backend).
const OUTREACH_STATUSES = [
  ['reached_out', 'Reached out'],
  ['in_conversation', 'In conversation'],
  ['agreement_signed', 'Agreement signed'],
  ['collaborated', 'Collaborated'],
  ['inactive', 'Inactive'],
];

const InfluencersPage = () => {
  const toast = useToast();
  const { showToast } = toast;
  const [filters, setFilters] = useState({
    search: '',
    collab_status: '',
    country: '',
  });

  const fetchFn = useCallback(
    (page, limit) => influencerService.getInfluencers({ page, limit, ...filters }),
    [filters],
  );
  const {
    items: influencers,
    loading,
    reset,
    sentinelRef,
  } = useInfiniteScroll(fetchFn, {
    limit: 50,
    deps: [filters],
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedInfluencer, setSelectedInfluencer] = useState(null);
  const [igPermResult, setIgPermResult] = useState(null);
  const [igPermLoading, setIgPermLoading] = useState(false);
  const [showIgPermModal, setShowIgPermModal] = useState(false);
  const [activeTab, setActiveTab] = useState('influencers');
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const selection = useRowSelection(
    influencers.map((i) => i.id),
    { resetKey: JSON.stringify(filters) },
  );
  const [showOutreachModal, setShowOutreachModal] = useState(false);
  const [showImportDMsModal, setShowImportDMsModal] = useState(false);

  const runBulkAction = async (action, params, verb) => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/influencers/bulk', { ids: selection.selectedIds, action, params });
      reportBulkResult(toast, result, verb, 'influencer');
      setConfirmBulkDelete(false);
      selection.clear();
      reset();
    } catch (error) {
      toast.error(error.message || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleCheckIgPermission = async () => {
    setIgPermLoading(true);
    setShowIgPermModal(true);
    try {
      const result = await api.get('/api/admin/posts/instagram/check-messaging-permission');
      setIgPermResult(result);
    } catch (err) {
      setIgPermResult({ error: err.message });
    } finally {
      setIgPermLoading(false);
    }
  };

  const handleRowClick = (influencer) => {
    setSelectedInfluencer(influencer);
  };

  const handleDrawerClose = () => {
    setSelectedInfluencer(null);
  };

  const handleDrawerUpdate = (updated) => {
    setSelectedInfluencer(updated);
    reset();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this influencer?')) return;
    try {
      await influencerService.deleteInfluencer(id);
      showToast('Influencer deleted', 'success');
      setSelectedInfluencer(null);
      reset();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Influencers
            {!loading && <span className="ml-3 text-lg font-normal text-muted-foreground">({influencers.length})</span>}
          </h1>
          <p className="text-muted-foreground mt-1">Manage influencer outreach and collaborations</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <button
            onClick={() => setShowImportDMsModal(true)}
            className="flex items-center gap-2 px-4 py-3 border border-input rounded-lg hover:bg-muted/50"
            title="Import influencers from Instagram DMs"
          >
            <Users size={18} className="text-pink-500" />
            Import from DMs
          </button>
          <button
            onClick={handleCheckIgPermission}
            disabled={igPermLoading}
            className="flex items-center gap-2 px-4 py-3 border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
            title="Check Instagram messaging permission"
          >
            <ShieldCheck
              size={20}
              className={igPermLoading ? 'animate-pulse text-blue-500' : 'text-muted-foreground'}
            />
            Check Instagram API
          </button>
          {activeTab === 'influencers' && (
            <button
              onClick={reset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-3 border border-input rounded-lg hover:bg-muted/50 disabled:opacity-50"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          )}
          {activeTab === 'influencers' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 transition"
            >
              <Plus size={20} />
              Add Influencer
            </button>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex border-b border-border mt-2 mb-6 overflow-x-auto">
        {[
          { id: 'influencers', label: 'Influencers', icon: Users },
          { id: 'pipeline', label: 'Pipeline', icon: GitBranch },
          { id: 'templates', label: 'Templates', icon: FileText },
          { id: 'insights', label: 'Insights', icon: TrendingUp },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-shrink-0 whitespace-nowrap flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition -mb-px ${
              activeTab === id
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Pipeline Tab */}
      {activeTab === 'pipeline' && <PipelineTab />}

      {/* Templates Tab */}
      {activeTab === 'templates' && <TemplatesTab />}

      {/* Insights Tab */}
      {activeTab === 'insights' && <InsightsTab />}

      {activeTab === 'influencers' && (
        <>
          {/* Filters */}
          <div className="bg-card rounded-lg shadow p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70"
                  size={20}
                />
                <input
                  type="text"
                  placeholder="Search by name, @handle, niche..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 pr-10 w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
                />
                {filters.search && (
                  <button
                    onClick={() => setFilters({ ...filters, search: '' })}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 hover:text-foreground transition-colors"
                    title="Clear search"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              <select
                value={filters.collab_status}
                onChange={(e) => setFilters({ ...filters, collab_status: e.target.value })}
                className="px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              >
                <option value="">All Statuses</option>
                {Object.entries(COLLAB_STATUS_LABELS).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>

              <select
                value={filters.country}
                onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                className="px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              >
                <option value="">All Countries</option>
                <option value="India">India</option>
                <option value="USA">USA</option>
                <option value="UK">UK</option>
                <option value="UAE">UAE</option>
                <option value="Singapore">Singapore</option>
                <option value="Australia">Australia</option>
                <option value="Other">Other</option>
              </select>

              <button
                onClick={() => setFilters({ search: '', collab_status: '', country: '' })}
                className="flex items-center justify-center gap-2 px-4 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
              >
                <RefreshCw size={16} />
                Reset
              </button>
            </div>
          </div>

          <BulkActionBar count={selection.count} onClear={selection.clear}>
            <BulkActionBar.Button onClick={() => setShowOutreachModal(true)}>
              <span className="inline-flex items-center gap-1.5">
                <Send size={12} />
                Send Outreach
              </span>
            </BulkActionBar.Button>
            <select
              aria-label="Set outreach status"
              value=""
              disabled={bulkBusy}
              onChange={(e) => runBulkAction('set_status', { status: e.target.value }, 'updated')}
              className="px-2 py-1.5 text-xs rounded-md bg-white/10 text-white border-0 disabled:opacity-50"
            >
              <option value="">Set status...</option>
              {OUTREACH_STATUSES.map(([value, label]) => (
                <option key={value} value={value} className="text-foreground">
                  {label}
                </option>
              ))}
            </select>
            <BulkActionBar.Button tone="danger" onClick={() => setConfirmBulkDelete(true)} disabled={bulkBusy}>
              Delete
            </BulkActionBar.Button>
          </BulkActionBar>

          {/* Content */}
          {loading && influencers.length === 0 ? (
            <LoadingScreen />
          ) : influencers.length === 0 ? (
            <div className="bg-card rounded-lg shadow p-12 text-center">
              <Users className="mx-auto text-muted-foreground/70 mb-4" size={48} />
              <h3 className="text-xl font-semibold text-foreground mb-2">No influencers found</h3>
              <p className="text-muted-foreground mb-6">Get started by adding your first influencer</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition"
              >
                Add Influencer
              </button>
            </div>
          ) : (
            /* ── Table View ── */
            <div className="bg-card rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-4 py-3 w-10">
                        <HeaderCheckbox selection={selection} />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Influencer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Followers
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Tier
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Niche
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {influencers.map((influencer) => (
                      <tr
                        key={influencer.id}
                        onClick={() => handleRowClick(influencer)}
                        className={`hover:bg-muted/50 cursor-pointer transition ${selection.isSelected(influencer.id) ? 'bg-blue-50 dark:bg-blue-950/50' : ''}`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <RowCheckbox
                            selection={selection}
                            id={influencer.id}
                            label={`Select ${influencer.name || 'influencer'}`}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            {influencer.profile_pic_url ? (
                              <img
                                src={influencer.profile_pic_url}
                                alt={influencer.name}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center text-sm font-semibold text-muted-foreground">
                                {getInitials(influencer.name)}
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-foreground">{influencer.name}</div>
                              {influencer.ig_username && (
                                <div className="text-xs text-muted-foreground">@{influencer.ig_username}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground/80 font-medium">
                          {formatFollowers(influencer.follower_count)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const tier = getTier(influencer.follower_count);
                            return tier ? (
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${tier.bg} ${tier.text}`}>
                                {tier.label}
                              </span>
                            ) : (
                              <span className="text-muted-foreground/70">—</span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {[influencer.city, influencer.country].filter(Boolean).join(', ') || '\u2014'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                          {influencer.niche || '\u2014'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {influencer.latest_collab_status ? (
                            <StatusBadge value={influencer.latest_collab_status} labels={COLLAB_STATUS_LABELS} />
                          ) : (
                            <StatusBadge value={influencer.outreach_status} labels={OUTREACH_LABELS} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="flex justify-center py-6">
            {loading && influencers.length > 0 && <LoadingSpinner size="sm" />}
          </div>
        </>
      )}

      {/* Drawer */}
      {selectedInfluencer && (
        <InfluencerDrawer
          influencer={selectedInfluencer}
          onClose={handleDrawerClose}
          onUpdate={handleDrawerUpdate}
          onDelete={handleDelete}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <InfluencerFormModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            reset();
          }}
          influencer={null}
        />
      )}

      {/* Import from DMs Modal */}
      {showImportDMsModal && <ImportFromDMsModal onClose={() => setShowImportDMsModal(false)} onImported={reset} />}

      {confirmBulkDelete && (
        <BulkConfirmModal
          title="Delete influencers"
          message="They are removed from the list and pipeline. Their collaboration history is kept."
          count={selection.count}
          confirmLabel="Delete"
          danger
          requireCount
          busy={bulkBusy}
          onConfirm={() => runBulkAction('delete', {}, 'deleted')}
          onClose={() => setConfirmBulkDelete(false)}
        />
      )}

      {/* Outreach Modal */}
      {showOutreachModal && (
        <OutreachModal
          influencers={influencers.filter((inf) => selection.isSelected(inf.id))}
          onClose={() => {
            setShowOutreachModal(false);
            selection.clear();
            reset();
          }}
        />
      )}

      {/* Instagram Permission Check Modal */}
      {showIgPermModal && (
        <Modal
          onClose={() => setShowIgPermModal(false)}
          maxWidth="lg"
          fullHeight={false}
          className="rounded-xl shadow-2xl"
        >
          <div className="flex justify-between items-center p-5 border-b border-border">
            <div className="flex items-center gap-2">
              <Instagram size={20} className="text-pink-500" />
              <h2 className="text-lg font-semibold">Instagram Messaging Permission</h2>
            </div>
            <button
              onClick={() => setShowIgPermModal(false)}
              className="text-muted-foreground/70 hover:text-foreground"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-5">
            {igPermLoading ? (
              <div className="flex flex-col items-center py-8 gap-3 text-muted-foreground">
                <LoadingSpinner />
                <p>Checking Instagram API permissions…</p>
              </div>
            ) : igPermResult ? (
              <div className="space-y-4">
                <div
                  className={`flex items-start gap-3 p-4 rounded-lg ${igPermResult.has_messaging_permission ? 'bg-green-50 dark:bg-green-950/50' : 'bg-red-50 dark:bg-red-950/50'}`}
                >
                  {igPermResult.has_messaging_permission ? (
                    <CheckCircle size={22} className="text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle size={22} className="text-red-500 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p
                      className={`font-semibold ${igPermResult.has_messaging_permission ? 'text-green-800 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}
                    >
                      {igPermResult.has_messaging_permission
                        ? 'instagram_manage_messages — Granted ✓'
                        : 'instagram_manage_messages — Not granted'}
                    </p>
                    {igPermResult.error && (
                      <p className="text-sm text-red-600 dark:text-red-400 mt-1">{igPermResult.error}</p>
                    )}
                  </div>
                </div>

                {igPermResult.conversations && (
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm font-medium text-foreground/80 mb-1">Conversations accessible</p>
                    <p className="text-2xl font-bold text-foreground">
                      {igPermResult.conversations.conversation_count}
                    </p>
                    {igPermResult.conversations.sample?.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">Chat read is ready to build.</p>
                    )}
                  </div>
                )}

                {igPermResult.granted_permissions?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-foreground/80 mb-2">All granted permissions</p>
                    <div className="flex flex-wrap gap-1.5">
                      {igPermResult.granted_permissions.map((p) => (
                        <span
                          key={p}
                          className={`px-2 py-1 text-xs rounded-full font-mono ${p === 'instagram_manage_messages' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 ring-1 ring-green-400' : 'bg-muted text-muted-foreground'}`}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <div className="px-5 pb-5">
            <button
              onClick={() => setShowIgPermModal(false)}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition text-sm font-medium"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Influencer Drawer ────────────────────────────────────────────────────────

const InfluencerDrawer = ({ influencer, onClose, onUpdate, onDelete }) => {
  const { showToast } = useToast();
  const [notes, setNotes] = useState(influencer.notes || '');
  const [collabs, setCollabs] = useState([]);
  const [loadingCollabs, setLoadingCollabs] = useState(false);
  const [showCollabModal, setShowCollabModal] = useState(false);
  const [editingCollab, setEditingCollab] = useState(null);
  const [dresses, setDresses] = useState([]);
  const [loadingDresses, setLoadingDresses] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    setNotes(influencer.notes || '');
  }, [influencer]);

  useEffect(() => {
    setLoadingCollabs(true);
    collaborationService
      .getCollaborations({ influencer_id: influencer.id })
      .then(setCollabs)
      .catch(() => {})
      .finally(() => setLoadingCollabs(false));

    setLoadingDresses(true);
    influencerService
      .getDresses(influencer.id)
      .then(setDresses)
      .catch(() => {})
      .finally(() => setLoadingDresses(false));
  }, [influencer.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadCollabs = () => {
    setLoadingCollabs(true);
    collaborationService
      .getCollaborations({ influencer_id: influencer.id })
      .then(setCollabs)
      .catch(() => {})
      .finally(() => setLoadingCollabs(false));
  };

  const handleNotesSave = async () => {
    if (notes === (influencer.notes || '')) return;
    try {
      const updated = await influencerService.updateInfluencer(influencer.id, { notes });
      onUpdate(updated);
      showToast('Notes saved', 'success');
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const latestCollab = collabs.length > 0 ? collabs.reduce((a, b) => (a.cycle_number > b.cycle_number ? a : b)) : null;

  return (
    <>
      <ModalDrawer onClose={onClose} maxWidth="lg" className="shadow-2xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
            <h2 className="text-xl font-bold text-foreground truncate">{influencer.name}</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-muted/50"
              >
                <Edit2 size={13} /> Edit
              </button>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Profile section */}
            <div className="flex items-start gap-4">
              {influencer.profile_pic_url ? (
                <img
                  src={influencer.profile_pic_url}
                  alt={influencer.name}
                  className="h-16 w-16 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-accent flex items-center justify-center text-lg font-bold text-muted-foreground flex-shrink-0">
                  {getInitials(influencer.name)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                {influencer.ig_username && (
                  <a
                    href={`https://instagram.com/${influencer.ig_username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-pink-600 dark:text-pink-400 hover:text-pink-800 dark:hover:text-pink-300 font-medium"
                  >
                    <Instagram size={14} />@{influencer.ig_username}
                    <ExternalLink size={12} />
                  </a>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  {formatFollowers(influencer.follower_count)} followers
                </p>
                <p className="text-sm text-muted-foreground">
                  {[influencer.city, influencer.country].filter(Boolean).join(', ') || '\u2014'}
                </p>
                {influencer.niche && <p className="text-xs text-muted-foreground mt-1">{influencer.niche}</p>}
                {influencer.engagement_rate && (
                  <p className="text-xs text-muted-foreground mt-0.5">Engagement: {influencer.engagement_rate}%</p>
                )}
                {latestCollab &&
                  (() => {
                    const cs = COLLAB_STATUS_LABELS[latestCollab.status];
                    return cs ? (
                      <div className="mt-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${cs.bg} ${cs.text}`}>
                          {cs.label} (Cycle #{latestCollab.cycle_number})
                        </span>
                      </div>
                    ) : null;
                  })()}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleNotesSave}
                rows={4}
                placeholder="Add notes about this influencer..."
                className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent resize-none"
              />
            </div>

            {/* Timeline */}
            {(influencer.contacted_at || influencer.collaborated_at) && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Timeline</h3>
                <div className="space-y-2 text-sm">
                  {influencer.contacted_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Contacted</span>
                      <span className="text-foreground">{new Date(influencer.contacted_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {influencer.collaborated_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Collaborated</span>
                      <span className="text-foreground">
                        {new Date(influencer.collaborated_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Collaborations */}
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Collaborations</h3>
                <button
                  onClick={() => {
                    setEditingCollab(null);
                    setShowCollabModal(true);
                  }}
                  className="flex items-center gap-1 text-sm text-foreground hover:underline"
                >
                  <Plus size={14} /> New
                </button>
              </div>
              {loadingCollabs ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : collabs.length === 0 ? (
                <p className="text-sm text-muted-foreground/70 text-center py-4 border border-dashed rounded-lg">
                  No collaborations yet
                </p>
              ) : (
                <div className="space-y-2">
                  {collabs.map((c) => {
                    const cs = COLLAB_STATUS_LABELS[c.status] || {
                      label: c.status,
                      bg: 'bg-muted',
                      text: 'text-muted-foreground',
                    };
                    const followUpDate = c.follow_up_date ? new Date(c.follow_up_date) : null;
                    const isOverdue = followUpDate && followUpDate < new Date();
                    return (
                      <div
                        key={c.id}
                        onClick={() => {
                          setEditingCollab(c);
                          setShowCollabModal(true);
                        }}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-accent"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">Cycle #{c.cycle_number}</p>
                          <p className="text-xs text-muted-foreground">
                            {c.agreed_rate
                              ? `${c.agreed_currency} ${parseFloat(c.agreed_rate).toLocaleString()}`
                              : 'No rate set'}
                            {followUpDate && (
                              <span className={`ml-2 ${isOverdue ? 'text-red-500 font-medium' : ''}`}>
                                \u00b7 Follow-up: {followUpDate.toLocaleDateString()}
                              </span>
                            )}
                          </p>
                          {c.deliverables?.length > 0 && (
                            <p className="text-xs text-muted-foreground/70 mt-0.5">
                              {c.deliverables.length} deliverable{c.deliverables.length > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                        <span
                          className={`px-2 py-1 text-xs rounded-full font-medium ${cs.bg} ${cs.text} flex-shrink-0 ml-2`}
                        >
                          {cs.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Dress History */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">Dress History</h3>
              {loadingDresses ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : dresses.length === 0 ? (
                <p className="text-sm text-muted-foreground/70 italic text-center py-4 border border-dashed border-border rounded-lg">
                  No dress history yet
                </p>
              ) : (
                <div className="space-y-2">
                  {dresses.map((d) => (
                    <div key={d.id} className="p-3 bg-muted/50 rounded-lg text-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-foreground">{d.dress_name || d.dress_sku || 'Unnamed'}</p>
                          {d.dress_sku && d.dress_name && (
                            <p className="text-xs text-muted-foreground/70">{d.dress_sku}</p>
                          )}
                        </div>
                        {d.status && (
                          <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full capitalize">
                            {d.status.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      {d.handoff_date && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Sent: {new Date(d.handoff_date).toLocaleDateString()}
                          {d.actual_return_date &&
                            ` \u00b7 Returned: ${new Date(d.actual_return_date).toLocaleDateString()}`}
                        </p>
                      )}
                      {d.content_urls?.length > 0 && (
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {d.content_urls.map((url, i) => (
                            <a
                              key={i}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              View content {i + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 flex-shrink-0">
            <button
              onClick={() => onDelete(influencer.id)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 border border-red-300 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/70 transition w-full justify-center"
            >
              <Trash2 size={16} />
              Delete Influencer
            </button>
          </div>
        </div>
      </ModalDrawer>

      {showEditModal && (
        <InfluencerFormModal
          influencer={influencer}
          onClose={() => setShowEditModal(false)}
          onSuccess={(updated) => {
            setShowEditModal(false);
            onUpdate(updated);
          }}
        />
      )}

      {showCollabModal && (
        <CollaborationModal
          influencer={influencer}
          collaboration={editingCollab}
          onClose={() => {
            setShowCollabModal(false);
            setEditingCollab(null);
          }}
          onSave={(saved) => {
            setShowCollabModal(false);
            setEditingCollab(null);
            reloadCollabs();
            // Propagate new latest_collab_status to parent list so the badge updates
            onUpdate({ ...influencer, latest_collab_status: saved.status });
          }}
        />
      )}
    </>
  );
};

// ─── Influencer Form Modal ────────────────────────────────────────────────────

const InfluencerFormModal = ({ onClose, onSuccess, influencer: editingInfluencer }) => {
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [lookupUsername, setLookupUsername] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupPreview, setLookupPreview] = useState(null);
  const isEdit = !!editingInfluencer;
  // Touched-since-open flag, reset after a successful save.
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormDataRaw] = useState({
    name: editingInfluencer?.name || '',
    ig_username: editingInfluencer?.ig_username || '',
    country: editingInfluencer?.country || '',
    city: editingInfluencer?.city || '',
    follower_count: editingInfluencer?.follower_count || '',
    niche: editingInfluencer?.niche || '',
    profile_pic_url: editingInfluencer?.profile_pic_url || '',
    notes: editingInfluencer?.notes || '',
    platform: editingInfluencer?.platform || 'instagram',
    engagement_rate: editingInfluencer?.engagement_rate || '',
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);

  // X icon and footer Cancel both dismiss the modal — confirm first if
  // there's anything to lose.
  const handleCloseClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    onClose();
  };

  const handleIgLookup = async () => {
    const username = lookupUsername.trim().replace('@', '');
    if (!username) return;
    setLookupLoading(true);
    setLookupPreview(null);
    try {
      const profile = await influencerService.lookupIgProfile(username);
      setLookupPreview(profile);
      // Auto-fill form fields from IG profile
      setFormData((prev) => ({
        ...prev,
        name: profile.name || prev.name,
        ig_username: profile.username || username,
        follower_count: profile.followers_count || prev.follower_count,
        profile_pic_url: profile.profile_picture_url || prev.profile_pic_url,
        notes: profile.biography
          ? `${prev.notes ? prev.notes + '\n' : ''}Bio: ${profile.biography}`.trim()
          : prev.notes,
      }));
      showToast(`Loaded @${profile.username}'s profile`, 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        follower_count: formData.follower_count ? parseInt(formData.follower_count, 10) : null,
        engagement_rate: formData.engagement_rate ? parseFloat(formData.engagement_rate) : null,
      };
      let result;
      if (isEdit) {
        result = await influencerService.updateInfluencer(editingInfluencer.id, payload);
        showToast('Influencer updated', 'success');
      } else {
        result = await influencerService.createInfluencer(payload);
        showToast('Influencer added successfully', 'success');
      }
      setIsDirty(false);
      onSuccess(result);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={handleCloseClick}
      maxWidth="2xl"
      closeOnBackdrop={false}
      className="rounded-lg"
      hasUnsavedChanges={isDirty}
    >
      <div className="sticky top-0 bg-card border-b px-6 py-4 flex justify-between items-center">
        <h2 className="text-2xl font-bold">{isEdit ? 'Edit Influencer' : 'Add Influencer'}</h2>
        <button onClick={handleCloseClick} className="text-muted-foreground hover:text-foreground">
          <X size={24} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {/* IG Profile Lookup — only on create */}
        {!isEdit && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-3 border border-border">
            <p className="text-sm font-medium text-foreground/80">Lookup from Instagram</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 text-sm">@</span>
                <input
                  type="text"
                  value={lookupUsername}
                  onChange={(e) => setLookupUsername(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleIgLookup())}
                  placeholder="username or paste IG profile URL"
                  className="w-full pl-7 pr-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
                />
              </div>
              <button
                type="button"
                onClick={handleIgLookup}
                disabled={lookupLoading || !lookupUsername.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {lookupLoading ? <RefreshCw size={14} className="animate-spin" /> : <Instagram size={14} />}
                {lookupLoading ? 'Fetching…' : 'Fetch'}
              </button>
            </div>
            {lookupPreview && (
              <div className="flex items-center gap-3 pt-1">
                {lookupPreview.profile_picture_url && (
                  <img
                    src={lookupPreview.profile_picture_url}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover flex-shrink-0"
                  />
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{lookupPreview.name}</p>
                  <p className="text-xs text-muted-foreground">
                    @{lookupPreview.username} · {lookupPreview.followers_count?.toLocaleString()} followers
                    {lookupPreview.media_count ? ` · ${lookupPreview.media_count} posts` : ''}
                  </p>
                </div>
                <span className="ml-auto text-xs text-green-600 dark:text-green-400 font-medium flex-shrink-0">
                  ✓ Filled
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground/70">
              Works for professional (business/creator) accounts. Fill in manually for personal accounts.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Instagram Username *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70">@</span>
              <input
                type="text"
                required
                value={formData.ig_username}
                onChange={(e) => setFormData({ ...formData, ig_username: e.target.value.replace('@', '') })}
                className="w-full pl-8 pr-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Country</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              placeholder="e.g., India, USA"
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Follower Count</label>
            <input
              type="number"
              value={formData.follower_count}
              onChange={(e) => setFormData({ ...formData, follower_count: e.target.value })}
              placeholder="e.g., 50000"
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Niche</label>
            <input
              type="text"
              value={formData.niche}
              onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
              placeholder="e.g., Fashion, Lifestyle"
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Engagement Rate (%)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.engagement_rate}
              onChange={(e) => setFormData({ ...formData, engagement_rate: e.target.value })}
              placeholder="e.g., 3.5"
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Platform</label>
            <select
              value={formData.platform}
              onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            >
              <option value="instagram">Instagram</option>
              <option value="youtube">YouTube</option>
              <option value="tiktok">TikTok</option>
              <option value="facebook">Facebook</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-foreground/80 mb-1">Profile Picture URL</label>
            <input
              type="url"
              value={formData.profile_pic_url}
              onChange={(e) => setFormData({ ...formData, profile_pic_url: e.target.value })}
              placeholder="https://example.com/photo.jpg"
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-foreground/80 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-input rounded-lg focus:ring-2 focus:ring-foreground focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={handleCloseClick}
            className="px-6 py-2 border border-input rounded-lg hover:bg-muted/50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
          >
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Influencer'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default InfluencersPage;
